/**
 * AppHeader — the portal's single top bar, used by every authenticated area.
 *
 * Before this there were two: `CommonHeader` (admin/moderator — opaque bar, page
 * title, solid logout button) and `StudentNav` (brand logo, nav links, gold
 * pills). They shared no markup, no stylesheet and no visual language, so the
 * two areas read as different products even after both were on the same tokens.
 *
 * The props cover both shapes rather than branching on area:
 *   - `title` names the screen (admin/moderator)
 *   - `nav` renders primary navigation (student)
 *   - `badge`, `user`, `onProfileClick`, `onSignOut` are all optional
 *
 * An area that needs something new adds a prop here, so both keep getting it.
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PonderLogo } from '../../components/ponder-logo';
import { LanguageSelect } from '../../components/language-select';
import styles from './app-header.module.css';

interface AppHeaderNavItem {
  key: string;
  label: string;
  active?: boolean;
  onClick: () => void;
}

interface AppHeaderProps {
  /** Screen name, shown after the brand. Omit when `nav` carries the context. */
  title?: string;
  /** Primary navigation. */
  nav?: AppHeaderNavItem[];
  /** Back affordance, before the title. */
  onBack?: () => void;
  backLabel?: string;
  /** Clicking the brand — usually "go home". Static when omitted. */
  onBrandClick?: () => void;
  /** Gold chip, e.g. the student's level. */
  badge?: ReactNode;
  /** Identity block. `meta` is a secondary line such as the role. */
  user?: { name: string; meta?: string; avatarUrl?: string | null };
  /** Makes the identity block a button (student opens the portfolio). */
  onProfileClick?: () => void;
  profileActive?: boolean;
  /** Trailing action — logout, or switch-profile on family devices. */
  onSignOut?: () => void;
  signOutLabel?: string;
}

export function AppHeader({
  title,
  nav,
  onBack,
  backLabel,
  onBrandClick,
  badge,
  user,
  onProfileClick,
  profileActive = false,
  onSignOut,
  signOutLabel,
}: AppHeaderProps) {
  const { t } = useTranslation();

  const profileBody = user && (
    <>
      {user.avatarUrl && <img src={user.avatarUrl} alt="" className={styles.avatar} />}
      <span className={styles.profileText}>
        <span className={styles.profileName}>{user.name}</span>
        {user.meta && <span className={styles.profileMeta}>{user.meta}</span>}
      </span>
      {onProfileClick && <span className={styles.chevron}>▾</span>}
    </>
  );

  return (
    <header className={styles.bar}>
      <div className={styles.inner}>
        <button
          type="button"
          className={styles.brand}
          onClick={onBrandClick}
          disabled={!onBrandClick}
          aria-label="Ponder"
        >
          <PonderLogo height={22} />
        </button>

        {onBack && (
          <button type="button" className={styles.back} onClick={onBack}>
            ← {backLabel ?? t('common.back')}
          </button>
        )}

        {title && (
          <>
            <span className={styles.titleRule} aria-hidden="true" />
            <h1 className={styles.title}>{title}</h1>
          </>
        )}

        {nav && nav.length > 0 && (
          <div className={styles.links}>
            {nav.map((item) => (
              <button
                key={item.key}
                type="button"
                className={[styles.link, item.active ? styles.linkActive : null]
                  .filter(Boolean)
                  .join(' ')}
                onClick={item.onClick}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}

        <span className={styles.spacer} />

        <div className={styles.actions}>
          {badge && <span className={styles.badge}>{badge}</span>}

          <LanguageSelect compact className={styles.lang} />

          {user &&
            (onProfileClick ? (
              <button
                type="button"
                className={[styles.profile, profileActive ? styles.profileActive : null]
                  .filter(Boolean)
                  .join(' ')}
                onClick={onProfileClick}
              >
                {profileBody}
              </button>
            ) : (
              <span className={`${styles.profile} ${styles.profileStatic}`}>{profileBody}</span>
            ))}

          {onSignOut && (
            <button type="button" className={styles.signOut} onClick={onSignOut}>
              {signOutLabel ?? t('dashboard.logout')}
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
